import React, { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Collapse,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { UNVERIFIED_REGISTRATION_STATUSES } from './registrationStatus';

export interface DeviceFilters {
  status?: string[];
  registrationStatus?: string[];
  clientVersion?: string[];
  hasSSHTunnel?: boolean;
  isOutdated?: boolean;
}

interface FilterPanelProps {
  filters: DeviceFilters;
  onFiltersChange: (filters: DeviceFilters) => void;
  availableVersions?: string[];
  latestVersion?: string;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  availableVersions = [],
  latestVersion,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleFilterChange = (key: keyof DeviceFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({});
    setExpanded(false);
  };

  const handleRemoveFilter = (key: keyof DeviceFilters) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    onFiltersChange(newFilters);
  };

  // Count active filters
  const activeFilterCount = Object.keys(filters).filter(
    (key) => filters[key as keyof DeviceFilters] !== undefined
  ).length;

  // Generate filter chips for display
  const getFilterChips = () => {
    const chips: { key: string; label: string; onDelete: () => void }[] = [];

    if (filters.status && filters.status.length > 0) {
      chips.push({
        key: 'status',
        label: `Status: ${filters.status.join(', ')}`,
        onDelete: () => handleRemoveFilter('status'),
      });
    }

    if (filters.registrationStatus && filters.registrationStatus.length > 0) {
      chips.push({
        key: 'registrationStatus',
        label: `Registration: ${filters.registrationStatus.join(', ')}`,
        onDelete: () => handleRemoveFilter('registrationStatus'),
      });
    }

    if (filters.clientVersion && filters.clientVersion.length > 0) {
      chips.push({
        key: 'clientVersion',
        label: `Version: ${filters.clientVersion.join(', ')}`,
        onDelete: () => handleRemoveFilter('clientVersion'),
      });
    }

    if (filters.hasSSHTunnel !== undefined) {
      chips.push({
        key: 'hasSSHTunnel',
        label: filters.hasSSHTunnel ? 'Has SSH Tunnel' : 'No SSH Tunnel',
        onDelete: () => handleRemoveFilter('hasSSHTunnel'),
      });
    }

    if (filters.isOutdated) {
      chips.push({
        key: 'isOutdated',
        label: 'Outdated Version',
        onDelete: () => handleRemoveFilter('isOutdated'),
      });
    }

    return chips;
  };

  const filterChips = getFilterChips();

  return (
    <Box sx={{ mb: 2 }}>
      {/* Filter Toggle and Chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Badge badgeContent={activeFilterCount} color="primary">
          <Tooltip title={expanded ? 'Hide filters' : 'Show filters'}>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              color={activeFilterCount > 0 ? 'primary' : 'default'}
              size="small"
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
        </Badge>

        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          Filters
        </Typography>

        {expanded && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}

        {/* Active filter chips */}
        {filterChips.map((chip) => (
          <Chip
            key={chip.key}
            label={chip.label}
            onDelete={chip.onDelete}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}

        {activeFilterCount > 0 && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            sx={{ ml: 'auto' }}
          >
            Clear All
          </Button>
        )}
      </Box>

      {/* Expandable Filter Controls */}
      <Collapse in={expanded}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} sx={{
            flexWrap: 'wrap'
          }}>
            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select<string[]>
                multiple
                value={filters.status || []}
                onChange={(e) =>
                  handleFilterChange('status', e.target.value as string[])
                }
                label="Status"
                renderValue={(selected) => selected.join(', ')}
              >
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </Select>
            </FormControl>

            {/* Registration Status Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Registration</InputLabel>
              <Select<string[]>
                multiple
                value={filters.registrationStatus || []}
                onChange={(e) =>
                  handleFilterChange('registrationStatus', e.target.value as string[])
                }
                label="Registration"
                renderValue={(selected) => selected.join(', ')}
              >
                <MenuItem value="enrolled">Enrolled</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="unverified_suspicious">Unverified (suspicious)</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            {/* Client Version Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Client Version</InputLabel>
              <Select<string[]>
                multiple
                value={filters.clientVersion || []}
                onChange={(e) =>
                  handleFilterChange('clientVersion', e.target.value as string[])
                }
                label="Client Version"
                renderValue={(selected) => selected.join(', ')}
              >
                {availableVersions.map((version) => (
                  <MenuItem key={version} value={version}>
                    {version}
                    {version === latestVersion && (
                      <Chip
                        label="Latest"
                        size="small"
                        color="success"
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Quick Filters */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                Quick:
              </Typography>
              <Button
                size="small"
                variant={filters.isOutdated ? 'contained' : 'outlined'}
                onClick={() =>
                  handleFilterChange('isOutdated', !filters.isOutdated)
                }
                sx={{ textTransform: 'none' }}
              >
                Outdated
              </Button>
              <Button
                size="small"
                variant={filters.hasSSHTunnel ? 'contained' : 'outlined'}
                onClick={() =>
                  handleFilterChange(
                    'hasSSHTunnel',
                    filters.hasSSHTunnel === true ? false : true
                  )
                }
                sx={{ textTransform: 'none' }}
              >
                SSH Active
              </Button>
            </Box>
          </Stack>

          {/* Smart Filters */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" gutterBottom sx={{
              color: 'text.secondary'
            }}>
              SMART FILTERS
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label="Needs Attention"
                onClick={() => {
                  onFiltersChange({
                    status: ['offline'],
                  });
                }}
                clickable
                size="small"
                variant="outlined"
              />
              <Chip
                label="New Devices"
                onClick={() => {
                  onFiltersChange({
                    registrationStatus: [...UNVERIFIED_REGISTRATION_STATUSES],
                  });
                }}
                clickable
                size="small"
                variant="outlined"
              />
              <Chip
                label="Update Candidates"
                onClick={() => {
                  onFiltersChange({
                    isOutdated: true,
                  });
                }}
                clickable
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default FilterPanel;
